// Flyout positioning constants
const DEFAULT_FLYOUT_HEIGHT = 200; // Default estimate
const DEFAULT_FLYOUT_WIDTH = 200; // Default estimate
const VIEWPORT_PADDING = 10; // Padding from viewport edges

/**
 * Calculates the optimal position for a flyout to ensure it's fully visible
 * @param buttonRect The button's bounding rectangle
 * @param flyoutElement Optional flyout element to get actual dimensions
 * @param flyoutWidth Fallback width if element not available
 * @param flyoutHeight Fallback height if element not available
 * @returns Position object with top, left, right values and isAbove flag
 */
export function calculateFlyoutPosition(
    buttonRect: DOMRect,
    flyoutElement?: HTMLElement,
    flyoutWidth: number = DEFAULT_FLYOUT_WIDTH,
    flyoutHeight: number = DEFAULT_FLYOUT_HEIGHT,
): { top: number; left: number; right: number; isAbove: boolean } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Use actual flyout dimensions if available, otherwise use defaults
    const actualWidth = flyoutElement ? flyoutElement.offsetWidth : flyoutWidth;
    const actualHeight = flyoutElement ? flyoutElement.offsetHeight : flyoutHeight;

    // Calculate if flyout fits below the button
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const fitsBelow = spaceBelow >= actualHeight + VIEWPORT_PADDING;
    const fitsAbove = spaceAbove >= actualHeight + VIEWPORT_PADDING;

    // Determine vertical position
    let top: number;
    let isAbove = false;

    if (fitsBelow) {
        // Position below button
        top = buttonRect.bottom;
    } else if (fitsAbove) {
        // Position above button
        top = buttonRect.top - actualHeight;
        isAbove = true;
    } else {
        // If it doesn't fit either way, position where there's more space
        if (spaceBelow > spaceAbove) {
            top = buttonRect.bottom;
        } else {
            top = buttonRect.top - actualHeight;
            isAbove = true;
        }
    }

    // Calculate horizontal position
    let left = buttonRect.left;
    let right = buttonRect.right;

    // Check if flyout extends beyond right edge of viewport
    if (buttonRect.left + actualWidth > viewportWidth - VIEWPORT_PADDING) {
        // Align flyout's right edge with button's right edge
        left = buttonRect.right - actualWidth;
        right = buttonRect.right;
    }

    // Ensure flyout doesn't extend beyond left edge
    if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
        right = left + actualWidth;
    }

    return { top, left, right, isAbove };
}

/**
 * Repositions a flyout after it's rendered using its actual dimensions
 * @param flyoutSelector CSS selector for the flyout element
 * @param buttonRect The button's bounding rectangle
 */
export function repositionFlyoutAfterRender(
    flyoutSelector: string,
    buttonRect: DOMRect,
): { top: number; left: number; right: number; isAbove: boolean } {
    const flyoutElement = document.querySelector(flyoutSelector) as HTMLElement;
    const position = calculateFlyoutPosition(buttonRect, flyoutElement);
    return {
        top: position.top,
        left: position.left,
        right: position.right,
        isAbove: position.isAbove,
    };
}
